<template>
    <ConfirmationModal
        additional-class-name="DeleteCustomPropertyModal"
        :title="title"
        size="md"
        :allow-close="!submitting"
        @close="$emit('close')"
    >
        <div v-if="loading" class="DeleteCustomPropertyModal__loading">
            <EidIcon :icon-src="faCircleNotch" alt="loading" class="animate-spin" />
            <p class="DeleteCustomPropertyModal__loadingLabel">
                Checking what uses this property…
            </p>
        </div>

        <p v-else-if="error" class="DeleteCustomPropertyModal__message">
            {{ error }}
        </p>

        <div v-else-if="usage">
            <template v-if="deletable">
                <p>
                    <strong>{{ propertyName }}</strong> will be permanently deleted. This cannot be undone.
                </p>
                <p v-if="usage.entityValueCount > 0">
                    {{ entityValueWarning }}
                </p>
            </template>

            <template v-else>
                <Alert v-if="usage.isCollateralsProperty" type="danger">
                    This property holds your collateral records and drives collateral evaluation across your
                    account. It cannot be deleted.
                </Alert>

                <template v-else>
                    <p class="DeleteCustomPropertyModal__intro">
                        <strong>{{ propertyName }}</strong> is still being used. Update everything listed
                        below to stop using it, then delete it.
                    </p>

                    <section v-if="usage.assignmentRules.length > 0" class="DeleteCustomPropertyModal__group">
                        <h4 class="DeleteCustomPropertyModal__groupTitle">
                            Risk profile assignment rules ({{ usage.assignmentRules.length }})
                        </h4>
                        <p class="DeleteCustomPropertyModal__groupHint">
                            These rules decide which entities the risk profile applies to using this
                            property's value.
                        </p>
                        <ul class="DeleteCustomPropertyModal__list">
                            <li v-for="item in usage.assignmentRules" :key="`rule-${item.ccgId}`">
                                <a :href="riskProfileHref(item.ccgId)" target="_blank" rel="noopener">
                                    {{ item.ccgDisplayName }}
                                </a>
                            </li>
                        </ul>
                    </section>

                    <section v-if="usage.criteria.length > 0" class="DeleteCustomPropertyModal__group">
                        <h4 class="DeleteCustomPropertyModal__groupTitle">
                            Risk profile criteria ({{ usage.criteria.length }})
                        </h4>
                        <p class="DeleteCustomPropertyModal__groupHint">
                            Each criterion below takes its value from this property. Clear its
                            <strong>Set To Field</strong> option, or point it at a different property.
                        </p>
                        <ul class="DeleteCustomPropertyModal__list">
                            <li v-for="profile in criteriaByRiskProfile" :key="`profile-${profile.ccgId}`">
                                <a :href="riskProfileHref(profile.ccgId)" target="_blank" rel="noopener">
                                    {{ profile.ccgDisplayName }}
                                </a>
                                <ul class="DeleteCustomPropertyModal__list">
                                    <li v-for="item in profile.criteria" :key="`criterion-${item.criterionId}`">
                                        {{ criterionLabel(item) }}
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </section>

                    <section v-if="usage.notificationTemplates.length > 0" class="DeleteCustomPropertyModal__group">
                        <h4 class="DeleteCustomPropertyModal__groupTitle">
                            Email notification templates ({{ usage.notificationTemplates.length }})
                        </h4>
                        <p class="DeleteCustomPropertyModal__groupHint">
                            These templates insert this property's value into the emails they send.
                        </p>
                        <ul class="DeleteCustomPropertyModal__list">
                            <li v-for="item in usage.notificationTemplates" :key="`template-${item}`">
                                <a :href="emailSettingsHref()" target="_blank" rel="noopener">
                                    {{ notificationLabel(item) }}
                                </a>
                            </li>
                        </ul>
                    </section>
                </template>
            </template>
        </div>

        <!-- Not loading, no error passed down, no usage: nothing to render but the dialog is
             already open, so say something rather than show an empty box. -->
        <p v-else class="DeleteCustomPropertyModal__message">
            {{ genericFailureMessage }}
        </p>
        <!-- ConfirmationModal's own `progress` mode swaps both buttons for a single primary one,
             which changes the dialog's shape and colour mid-submit. Rendering the footer keeps the
             buttons put and lets NewButton show its spinner in place. -->
        <template #footer>
            <NewButton
                v-if="deletable"
                variant="outline"
                color="secondary"
                :disabled="submitting"
                @click="$emit('close')"
            >
                Cancel
            </NewButton>
            <NewButton
                variant="outline"
                :color="confirmColor"
                :loading="submitting"
                @click="onConfirm"
            >
                {{ confirmLabel }}
            </NewButton>
        </template>
    </ConfirmationModal>
</template>

<style lang="scss">
.DeleteCustomPropertyModal {
    // Loading carries far less content than the blocked list; without a floor the dialog
    // visibly shrinks and re-grows as the usage arrives.
    .Modal__content {
        min-height: 8rem;
    }

    &__loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 0.75rem;
        min-height: 8rem;
    }

    &__loadingLabel {
        color: var(--eidQualityBlack-70);
    }

    // A message that is the whole body reads as content, not as a callout on top of content.
    &__message {
        margin: 0;
    }

    &__intro {
        margin-bottom: 1rem;
    }

    &__group {
        margin-bottom: 1.25rem;

        // Sections after the first get a rule, so several categories read as separate blocks
        // rather than one run-on list.
        & + & {
            border-top: 1px solid var(--eidTechGray-25);
            padding-top: 1.25rem;
        }
    }

    &__groupHint {
        color: var(--eidQualityBlack-70);
        font-size: var(--text-small-size);
        margin-bottom: 0.5rem;
    }

    &__groupTitle {
        // h1-h5 are Roboto Condensed globally; these headings sit inside body copy and should
        // read as part of it.
        font-family: inherit;
        font-size: var(--text-section-header-size);
        font-weight: var(--text-section-header-weight);
        line-height: var(--text-section-header-line-height);
        color: var(--eidSecureBlue);
        margin-bottom: 0.25rem;
    }

    &__list {
        list-style: disc;
        padding-left: 1.25rem;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faCircleNotch } from '@fortawesome/pro-regular-svg-icons';

    import Alert from '@evidentid/dashboard-commons/components/Alert/Alert.vue';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { CustomProperty, CustomPropertyCriterionUsage, CustomPropertyUsage, isCustomPropertyDeletable }
        from '@evidentid/tprm-portal-lib/models/dashboard';
    import { NotificationType } from '@evidentid/tprm-portal-lib/models/notification-configuration';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import { getNotificationTypeLabel }
        from '@/modules/notifications-configuration/utils/getNotificationTypeLabel';

    interface CriteriaByRiskProfile {
        ccgId: string;
        ccgDisplayName: string;
        criteria: CustomPropertyCriterionUsage[];
    }

    /**
     * Renders one of three states from the same usage object: still loading, blocked by
     * dependencies, or safe to delete. Deletability is never decided here — it is derived from
     * whatever the server last said, so a 409 can swap this straight into the blocked state.
     */
    export default defineComponent({
        name: 'DeleteCustomPropertyModal',
        components: { Alert, ConfirmationModal, EidIcon, NewButton },
        props: {
            property: {
                type: Object as PropType<CustomProperty | null>,
                default: null,
            },
            usage: {
                type: Object as PropType<CustomPropertyUsage | null>,
                default: null,
            },
            rpName: {
                type: String as PropType<string>,
                required: true,
            },
            loading: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            submitting: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            error: {
                type: String as PropType<string | null>,
                default: null,
            },
        },
        emits: [ 'close', 'confirm' ],
        data() {
            return {
                faCircleNotch,
                genericFailureMessage: 'Something went wrong. This property cannot be deleted right now.',
            };
        },
        computed: {
            propertyName(): string {
                return this.property?.name || 'This property';
            },
            deletable(): boolean {
                // Without a usage we cannot offer a delete: an unknown dependency state must not
                // present an action the server would only reject. A failed request is deliberately
                // not part of this — a 409 refreshes `usage`, which is what expresses a genuine
                // block, so folding `error` in here would strip Cancel and the retry away from
                // anyone who just hit a transient 500.
                return Boolean(this.usage) && !this.loading
                    && isCustomPropertyDeletable(this.usage as CustomPropertyUsage);
            },
            title(): string {
                return this.deletable ? 'Delete custom property' : 'Cannot delete this property';
            },
            confirmLabel(): string {
                return this.deletable ? 'Delete Property' : 'Close';
            },
            /** Only the destructive action is styled as destructive; Close is a dismissal. */
            confirmColor(): string {
                return this.deletable ? 'danger' : 'secondary';
            },
            /**
             * A risk profile can hold many criteria across many requirements, so criteria are
             * listed under the profile they belong to rather than repeating its name per row.
             */
            criteriaByRiskProfile(): CriteriaByRiskProfile[] {
                const byProfile = new Map<string, CriteriaByRiskProfile>();
                (this.usage?.criteria ?? []).forEach((item) => {
                    const existing = byProfile.get(item.ccgId);
                    if (existing) {
                        existing.criteria.push(item);
                    } else {
                        byProfile.set(item.ccgId, {
                            ccgId: item.ccgId,
                            ccgDisplayName: item.ccgDisplayName,
                            criteria: [ item ],
                        });
                    }
                });
                return [ ...byProfile.values() ];
            },
            producerFlowEnabled(): boolean {
                return (this as any).$rp?.currentDetails?.producerFlowEnabled || false;
            },
            /**
             * The count covers active entities only, but the delete clears stored values for
             * deactivated ones as well, so the wording says so rather than implying the count is
             * the whole extent of the data loss.
             */
            entityValueWarning(): string {
                const count = this.usage?.entityValueCount ?? 0;
                const entities = count === 1 ? '1 active entity' : `${count} active entities`;
                return `${entities} currently hold a value for this property. Those values will be `
                    + 'deleted too, along with any held by deactivated entities.';
            },
        },
        methods: {
            /**
             * Falls back to the raw field and coverage type when the human names are absent.
             * The two repos deploy independently, so a frontend release can lead the backend
             * that supplies those names — and a dependency the user cannot read is no more
             * useful than one that is missing.
             */
            /** Names a template exactly as the Email Notifications page does, so the user can find it. */
            notificationLabel(type: string): string {
                return getNotificationTypeLabel(type as NotificationType, this.producerFlowEnabled);
            },
            criterionLabel(item: CustomPropertyCriterionUsage): string {
                const criterion = item.criterionTitle || item.field;
                const requirement = item.requirementName || item.coverageType;
                return requirement ? `${criterion} — ${requirement}` : criterion;
            },
            riskProfileHref(ccgId: string): string {
                return this.$router.resolve({
                    name: 'decisioningRiskProfile',
                    params: { rpId: this.rpName, id: ccgId },
                }).href;
            },
            emailSettingsHref(): string {
                return this.$router.resolve({
                    name: 'notifications-email-settings',
                    params: { rpId: this.rpName },
                }).href;
            },
            onConfirm(): void {
                // in the blocked and failure states this button is a Close, not a confirm
                this.$emit(this.deletable ? 'confirm' : 'close');
            },
        },
    });
</script>
